/**
 * Resume Generation Service
 * 
 * This service loads resume templates from Markdown files that can be edited by the backend.
 * The template is located at: /public/templates/resume-template.md
 */

export interface ResumeGenerationInput {
  jobDescription: string;
}

export interface GeneratedResume {
  content: string; // Lexical JSON state
  htmlContent: string; // HTML version for preview
}

/**
 * Template URL - can be changed to point to a different template
 * Backend can serve different templates based on requirements
 */
const TEMPLATE_URL = '/templates/resume-template.md';

/**
 * Generate resume content based on JD
 * 
 * Fetches the Markdown template from the server and converts it to Lexical format.
 * The backend can modify the template at /public/templates/resume-template.md
 * 
 * For custom API integration:
 * ```typescript
 * const response = await fetch('/api/generate-resume', {
 *   method: 'POST',
 *   headers: { 'Content-Type': 'application/json' },
 *   body: JSON.stringify({ jobDescription: input.jobDescription }),
 * });
 * const data = await response.json();
 * // data.markdown contains the Markdown content
 * const lexicalContent = parseMarkdownToLexical(data.markdown);
 * return { content: JSON.stringify(lexicalContent), htmlContent: '' };
 * ```
 */
export async function generateResumeContent(
  input: ResumeGenerationInput
): Promise<GeneratedResume> {
  try {
    // Fetch the Markdown template from the server
    const response = await fetch(TEMPLATE_URL);
    
    if (!response.ok) {
      console.warn('Failed to fetch template, using fallback');
      return generateFallbackContent(input);
    }
    
    const markdownContent = await response.text();
    
    // Convert Markdown to Lexical format
    const lexicalContent = parseMarkdownToLexical(markdownContent);
    
    return {
      content: JSON.stringify(lexicalContent),
      htmlContent: '',
    };
  } catch (error) {
    console.error('Error loading template:', error);
    return generateFallbackContent(input);
  }
}

/**
 * Fallback content generation if template fetch fails
 */
async function generateFallbackContent(input: ResumeGenerationInput): Promise<GeneratedResume> {
  const lexicalContent = createFormattedResumeState(input.jobDescription);
  return {
    content: JSON.stringify(lexicalContent),
    htmlContent: '',
  };
}

/**
 * Parse Markdown content to Lexical JSON format
 * 
 * Supports:
 * - # H1, ## H2, ### H3 headings
 * - **bold** and *italic* text
 * - - bullet lists
 * - Regular paragraphs
 * - Empty lines for spacing
 * - Alignment: {center}, {left}, {right}, {justify} at end of line
 * 
 * Alignment Examples:
 * - # YOUR NAME {center}          -> Centered H1 heading
 * - ## SKILLS {left}              -> Left-aligned H2 heading
 * - Some paragraph text {right}   -> Right-aligned paragraph
 */
export function parseMarkdownToLexical(markdown: string): object {
  const lines = markdown.split('\n');
  const children: any[] = [];
  let currentListItems: any[] = [];
  let i = 0;

  const createText = (text: string, format: number = 0) => ({
    type: 'text',
    text,
    format,
    mode: 'normal',
    style: '',
    detail: 0,
    version: 1,
  });

  /**
   * Extract alignment directive from line
   * Returns { content: string without directive, align: alignment value }
   */
  const extractAlignment = (line: string): { content: string; align: string } => {
    const alignMatch = line.match(/\s*\{(center|left|right|justify)\}\s*$/i);
    if (alignMatch) {
      return {
        content: line.slice(0, alignMatch.index).trim(),
        align: alignMatch[1].toLowerCase(),
      };
    }
    return { content: line, align: '' };
  };

  const parseInlineFormatting = (text: string): any[] => {
    const textNodes: any[] = [];
    let remaining = text;
    
    while (remaining.length > 0) {
      // Check for bold **text**
      const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
      if (boldMatch) {
        textNodes.push(createText(boldMatch[1], 1)); // 1 = bold
        remaining = remaining.slice(boldMatch[0].length);
        continue;
      }
      
      // Check for italic *text*
      const italicMatch = remaining.match(/^\*([^*]+)\*/);
      if (italicMatch) {
        textNodes.push(createText(italicMatch[1], 2)); // 2 = italic
        remaining = remaining.slice(italicMatch[0].length);
        continue;
      }
      
      // Find next special character
      const nextSpecial = remaining.search(/\*\*|\*/);
      if (nextSpecial === -1) {
        // No more special characters
        if (remaining) textNodes.push(createText(remaining));
        break;
      } else if (nextSpecial === 0) {
        // Unmatched special char, treat as text
        textNodes.push(createText(remaining[0]));
        remaining = remaining.slice(1);
      } else {
        // Add text before special char
        textNodes.push(createText(remaining.slice(0, nextSpecial)));
        remaining = remaining.slice(nextSpecial);
      }
    }
    
    return textNodes.length > 0 ? textNodes : [createText('')];
  };

  const flushListItems = () => {
    if (currentListItems.length > 0) {
      children.push({
        type: 'list',
        listType: 'bullet',
        start: 1,
        tag: 'ul',
        children: currentListItems,
        direction: 'ltr',
        format: '',
        indent: 0,
        version: 1,
      });
      currentListItems = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i];
    
    // H1 heading
    if (line.startsWith('# ')) {
      flushListItems();
      const { content, align } = extractAlignment(line.slice(2));
      children.push({
        type: 'heading',
        tag: 'h1',
        children: [createText(content.trim(), 1)], // Bold
        direction: 'ltr',
        format: align || '', // Use extracted alignment or default
        indent: 0,
        version: 1,
      });
    }
    // H2 heading
    else if (line.startsWith('## ')) {
      flushListItems();
      const { content, align } = extractAlignment(line.slice(3));
      children.push({
        type: 'heading',
        tag: 'h2',
        children: [createText(content.trim(), 1)], // Bold
        direction: 'ltr',
        format: align || '', // Use extracted alignment
        indent: 0,
        version: 1,
      });
    }
    // H3 heading
    else if (line.startsWith('### ')) {
      flushListItems();
      const { content, align } = extractAlignment(line.slice(4));
      children.push({
        type: 'heading',
        tag: 'h3',
        children: [createText(content.trim(), 1)], // Bold
        direction: 'ltr',
        format: align || '', // Use extracted alignment
        indent: 0,
        version: 1,
      });
    }
    // Bullet list item
    else if (line.startsWith('- ') || line.startsWith('* ')) {
      const { content, align } = extractAlignment(line.slice(2));
      currentListItems.push({
        type: 'listitem',
        children: parseInlineFormatting(content.trim()),
        direction: 'ltr',
        format: align || '',
        indent: 0,
        value: currentListItems.length + 1,
        version: 1,
      });
    }
    // Explicit blank line marker "--" or empty line - creates visible blank space
    else if (line.trim() === '' || line.trim() === '--') {
      flushListItems();
      children.push({
        type: 'paragraph',
        children: [
          {
            type: 'text',
            text: '\u200B', // Zero-width space to ensure paragraph renders
            format: 0,
            style: '',
            mode: 'normal',
            detail: 0,
            version: 1,
          }
        ],
        direction: 'ltr',
        format: '',
        indent: 0,
        version: 1,
      });
    }
    // Regular paragraph with inline formatting
    else {
      flushListItems();
      const { content, align } = extractAlignment(line);
      
      children.push({
        type: 'paragraph',
        children: parseInlineFormatting(content),
        direction: 'ltr',
        format: align || '', // Use extracted alignment
        indent: 0,
        version: 1,
      });
    }
    
    i++;
  }

  flushListItems();

  return {
    root: {
      type: 'root',
      children,
      direction: 'ltr',
      format: '',
      indent: 0,
      version: 1,
    },
  };
}

/**
 * Create a formatted resume template with proper alignments
 * - Name: centered, bold, large
 * - Contact: centered
 * - Section headings: left-aligned, bold
 * - Body content: left-aligned
 */
function createFormattedResumeState(jd: string): object {
  const children: any[] = [];

  const createText = (text: string, format: number = 0, style: string = '') => ({
    type: 'text',
    text,
    format,
    mode: 'normal',
    style,
    detail: 0,
    version: 1,
  });

  const createParagraph = (textChildren: any[], align: string = '') => ({
    type: 'paragraph',
    children: textChildren,
    direction: 'ltr',
    format: align,
    indent: 0,
    version: 1,
  });

  const createHeading = (text: string, tag: 'h1' | 'h2' | 'h3', align: string = '', style: string = '') => ({
    type: 'heading',
    tag,
    children: [createText(text, 1, style)], // 1 = bold
    direction: 'ltr',
    format: align,
    indent: 0,
    version: 1,
  });

  const createList = (items: string[]) => ({
    type: 'list',
    listType: 'bullet',
    start: 1,
    tag: 'ul',
    children: items.map((item, i) => ({
      type: 'listitem',
      children: [createText(item)],
      direction: 'ltr',
      format: '',
      indent: 0,
      value: i + 1,
      version: 1,
    })),
    direction: 'ltr',
    format: '',
    indent: 0,
    version: 1,
  });

  // === HEADER SECTION (Centered) ===
  // Name - Large, Bold, Centered
  children.push(createHeading('YOUR NAME', 'h1', 'center'));

  // Contact Info - Centered
  children.push(createParagraph(
    [createText('email@example.com | (123) 456-7890 | City, State | linkedin.com/in/yourprofile')],
    'center'
  ));

  // Divider line (empty paragraph)
  children.push(createParagraph([]));

  // === PROFESSIONAL SUMMARY ===
  children.push(createHeading('PROFESSIONAL SUMMARY', 'h2', ''));
  children.push(createParagraph([
    createText(
      jd.length > 100
        ? 'Results-driven professional with expertise aligned to the requirements of this role. Proven track record of delivering impactful results and leading successful initiatives in relevant areas.'
        : 'Highly motivated professional seeking to leverage skills and experience to contribute effectively to the team. Strong background in [relevant field] with a focus on delivering quality results.'
    ),
  ]));
  children.push(createParagraph([]));

  // === PROFESSIONAL EXPERIENCE ===
  children.push(createHeading('PROFESSIONAL EXPERIENCE', 'h2', ''));
  children.push(createParagraph([]));

  // Job 1
  children.push(createParagraph([
    createText('[Job Title]', 1), // Bold
    createText(' | '),
    createText('[Company Name]', 1),
  ]));
  children.push(createParagraph([
    createText('[Start Date] - Present | [Location]', 2), // Italic
  ]));
  children.push(createList([
    'Led initiatives that directly align with requirements outlined in the job description',
    'Achieved measurable outcomes demonstrating relevant competencies and expertise',
    'Collaborated with cross-functional teams to deliver successful projects on time',
    'Implemented solutions that improved efficiency and quality metrics by [X]%',
  ]));
  children.push(createParagraph([]));

  // Job 2
  children.push(createParagraph([
    createText('[Previous Job Title]', 1),
    createText(' | '),
    createText('[Previous Company]', 1),
  ]));
  children.push(createParagraph([
    createText('[Start Date] - [End Date] | [Location]', 2),
  ]));
  children.push(createList([
    'Developed expertise in areas relevant to target position requirements',
    'Contributed to projects requiring key skills mentioned in job description',
    'Demonstrated ability to adapt and learn in dynamic environments',
  ]));
  children.push(createParagraph([]));

  // === EDUCATION ===
  children.push(createHeading('EDUCATION', 'h2', ''));
  children.push(createParagraph([]));
  children.push(createParagraph([
    createText('[Degree Name]', 1),
    createText(' | '),
    createText('[University Name]'),
  ]));
  children.push(createParagraph([
    createText('[Graduation Year] | [Location]', 2),
  ]));
  children.push(createParagraph([]));

  // === SKILLS ===
  children.push(createHeading('SKILLS', 'h2', ''));
  children.push(createParagraph([
    createText('Technical: ', 1),
    createText('[Skill 1], [Skill 2], [Skill 3], [Skill 4], [Skill 5]'),
  ]));
  children.push(createParagraph([
    createText('Tools: ', 1),
    createText('[Tool 1], [Tool 2], [Tool 3], [Tool 4]'),
  ]));
  children.push(createParagraph([
    createText('Soft Skills: ', 1),
    createText('Communication, Leadership, Problem-solving, Team Collaboration'),
  ]));
  children.push(createParagraph([]));

  // === CERTIFICATIONS ===
  children.push(createHeading('CERTIFICATIONS', 'h2', ''));
  children.push(createList([
    '[Certification Name] - [Issuing Organization], [Year]',
    '[Certification Name] - [Issuing Organization], [Year]',
  ]));

  return {
    root: {
      type: 'root',
      children,
      direction: 'ltr',
      format: '',
      indent: 0,
      version: 1,
    },
  };
}

/**
 * Create a Lexical editor state from text content (legacy, kept for reference)
 */
function createLexicalState(sections: string[]): object {
  const children: any[] = [];
  let currentListItems: any[] = [];

  const flushListItems = () => {
    if (currentListItems.length > 0) {
      children.push({
        type: 'list',
        listType: 'bullet',
        start: 1,
        tag: 'ul',
        children: currentListItems,
        direction: 'ltr',
        format: '',
        indent: 0,
        version: 1,
      });
      currentListItems = [];
    }
  };

  sections.forEach((line) => {
    if (line.startsWith('# ')) {
      flushListItems();
      children.push({
        type: 'heading',
        tag: 'h1',
        children: [{ type: 'text', text: line.substring(2), format: 0, mode: 'normal', style: '', detail: 0, version: 1 }],
        direction: 'ltr',
        format: '',
        indent: 0,
        version: 1,
      });
    } else if (line.startsWith('## ')) {
      flushListItems();
      children.push({
        type: 'heading',
        tag: 'h2',
        children: [{ type: 'text', text: line.substring(3), format: 0, mode: 'normal', style: '', detail: 0, version: 1 }],
        direction: 'ltr',
        format: '',
        indent: 0,
        version: 1,
      });
    } else if (line.startsWith('• ')) {
      currentListItems.push({
        type: 'listitem',
        children: [{ type: 'text', text: line.substring(2), format: 0, mode: 'normal', style: '', detail: 0, version: 1 }],
        direction: 'ltr',
        format: '',
        indent: 0,
        value: currentListItems.length + 1,
        version: 1,
      });
    } else if (line.startsWith('**') && line.includes('**')) {
      flushListItems();
      const boldMatch = line.match(/\*\*([^*]+)\*\*/);
      const textChildren: any[] = [];
      if (boldMatch) {
        const parts = line.split(/\*\*[^*]+\*\*/);
        if (parts[0]) {
          textChildren.push({ type: 'text', text: parts[0], format: 0, mode: 'normal', style: '', detail: 0, version: 1 });
        }
        textChildren.push({ type: 'text', text: boldMatch[1], format: 1, mode: 'normal', style: '', detail: 0, version: 1 });
        if (parts[1]) {
          textChildren.push({ type: 'text', text: parts[1], format: 0, mode: 'normal', style: '', detail: 0, version: 1 });
        }
      }
      children.push({
        type: 'paragraph',
        children: textChildren.length > 0 ? textChildren : [{ type: 'text', text: line, format: 0, mode: 'normal', style: '', detail: 0, version: 1 }],
        direction: 'ltr',
        format: '',
        indent: 0,
        version: 1,
      });
    } else if (line.startsWith('*') && line.endsWith('*') && line.length > 2) {
      flushListItems();
      children.push({
        type: 'paragraph',
        children: [{ type: 'text', text: line.slice(1, -1), format: 2, mode: 'normal', style: '', detail: 0, version: 1 }],
        direction: 'ltr',
        format: '',
        indent: 0,
        version: 1,
      });
    } else if (line.trim() === '') {
      flushListItems();
      children.push({
        type: 'paragraph',
        children: [],
        direction: 'ltr',
        format: '',
        indent: 0,
        version: 1,
      });
    } else {
      flushListItems();
      children.push({
        type: 'paragraph',
        children: [{ type: 'text', text: line, format: 0, mode: 'normal', style: '', detail: 0, version: 1 }],
        direction: 'ltr',
        format: '',
        indent: 0,
        version: 1,
      });
    }
  });

  flushListItems();

  return {
    root: {
      type: 'root',
      children,
      direction: 'ltr',
      format: '',
      indent: 0,
      version: 1,
    },
  };
}

/**
 * Check if content generation is ready
 * Returns true if there's enough input to generate content (JD text only)
 */
export function canGenerateResume(input: ResumeGenerationInput): boolean {
  return input.jobDescription.trim().length >= 10;
}
